import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { TradeEntry, MistakeAnalysis } from "../../types/tradingJournal";

interface MistakeTrackerProps {
  entries: TradeEntry[];
}

const MistakeTracker = ({ entries }: MistakeTrackerProps) => {
  const mistakeAnalysis = useMemo(() => {
    // Collect all mistakes
    const mistakesMap = new Map<string, MistakeAnalysis>();
    let totalTrades = entries.length;

    entries.forEach((entry) => {
      if (entry.mistakes && entry.mistakes.length > 0) {
        entry.mistakes.forEach((mistake) => {
          const existing = mistakesMap.get(mistake);

          if (existing) {
            existing.count += 1;
            if (entry.pnl < 0) {
              existing.totalLoss += Math.abs(entry.pnl);
            }
          } else {
            mistakesMap.set(mistake, {
              name: mistake,
              count: 1,
              totalLoss: entry.pnl < 0 ? Math.abs(entry.pnl) : 0,
              percentageOfTrades: 0,
            });
          }
        });
      }
    });

    // Calculate percentages
    mistakesMap.forEach((mistake) => {
      mistake.percentageOfTrades = (mistake.count / totalTrades) * 100;
    });

    // Convert to array and sort by count (descending)
    return Array.from(mistakesMap.values()).sort((a, b) => b.count - a.count);
  }, [entries]);

  if (mistakeAnalysis.length === 0) {
    return (
      <Card className="intellectia-card">
        <CardHeader>
          <CardTitle>Mistake Analysis</CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <p className="text-white/60">
            No mistakes have been tagged in your trades yet.
          </p>
          <p className="text-white/60 mt-2">
            Tag mistakes in your trades to see analysis here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="intellectia-card">
      <CardHeader>
        <CardTitle>Mistake Analysis</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={mistakeAnalysis}
                margin={{ top: 10, right: 10, left: 10, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  tick={{ fill: "#aaa", fontSize: 12 }}
                  height={70}
                />
                <YAxis tick={{ fill: "#aaa" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1a1a1a",
                    borderColor: "#333",
                    borderRadius: "6px",
                  }}
                />
                <Bar dataKey="count" name="Occurrence" radius={[4, 4, 0, 0]}>
                  {mistakeAnalysis.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill="#ef4444" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4">Mistake</th>
                  <th className="text-center py-3 px-4">Occurrences</th>
                  <th className="text-center py-3 px-4">% of Trades</th>
                  <th className="text-right py-3 px-4">Total Loss</th>
                </tr>
              </thead>
              <tbody>
                {mistakeAnalysis.map((mistake, index) => (
                  <tr
                    key={index}
                    className="border-b border-white/5 hover:bg-white/5"
                  >
                    <td className="py-3 px-4">{mistake.name}</td>
                    <td className="text-center py-3 px-4">{mistake.count}</td>
                    <td className="text-center py-3 px-4">
                      {mistake.percentageOfTrades.toFixed(1)}%
                    </td>
                    <td className="text-right py-3 px-4 text-red-400">
                      ₹{mistake.totalLoss.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MistakeTracker;
